import { useState } from 'react';
import { UMIcon } from '../../urbanmap/_components';
import { StationList } from './StationList';
import { nearestStation } from './domain';
import { PC, UMR, caption, card } from './tokens';
import type { LngLat, WeatherStation } from './types';

interface Props {
  stations: WeatherStation[];
  selected: string;
  /** Centre du périmètre : null tant qu'aucun bâtiment n'est inclus. */
  center: LngLat | null;
  /** Le choix n'est proposé qu'une fois le périmètre renseigné. */
  ready: boolean;
  onSelect: (id: string) => void;
}

/** Section 3 de l'étape 1 : station météo du projet, en champ dépliable (carte ou liste). */
export function StationSection({ stations, selected, center, ready, onSelect }: Props) {
  const [open, setOpen] = useState(false);

  if (!ready) {
    return (
      <div style={{ ...card, background: UMR.grey100, ...caption }}>
        Renseignez d’abord le périmètre : les stations météo s’affichent alors sur la carte.
      </div>
    );
  }

  const nearest = nearestStation(center, stations);
  const current = stations.find((s) => s.id === selected) ?? nearest;
  const pick = (id: string) => { onSelect(id); setOpen(false); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label="Station météo du projet"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          cursor: 'pointer', padding: '4px 8px', background: UMR.white,
          border: `1px solid ${open ? PC.accentBorder : UMR.border}`, font: `400 13px/18px ${UMR.font}`,
          color: UMR.black,
        }}
      >
        <UMIcon name="thermostat" size={18} color={PC.accent} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {current.name}
          </span>
          <span style={{ ...caption, fontVariantNumeric: 'tabular-nums' }}>DJ standards {current.dju} / {current.djclim}</span>
        </span>
        <UMIcon name={open ? 'expand_less' : 'expand_more'} size={20} color={UMR.textSecondary} />
      </button>

      {open && (
        <>
          <div style={caption}>
            Choisissez la station dans la liste, ou cliquez son point sur la carte pour voir ses données.
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 2 }}>
            <StationList stations={stations} selected={selected} center={center} nearestId={nearest.id} onSelect={pick} />
          </div>
        </>
      )}
      {!open && (
        <div style={caption}>
          Ses degrés-jours serviront à calibrer les ratios à l’étape suivante.
        </div>
      )}
    </div>
  );
}
